import { useCallback } from 'react'

import { useAppUserContext } from 'app/useAppUserContext'
import { useIsFieldEditable } from 'features/views/attributes/editable/hooks/useIsFieldEditable'
import { useDetailViewContext } from 'features/views/DetailView/hooks/useDetailViewContext'
import {
    DetailViewHeader,
    DetailViewHeaderFieldRef,
    DetailViewHeaderFileRef,
    DetailViewHeaderSpacious,
} from 'features/views/DetailView/types'

type UseDetailViewHeaderCoverImageInlineEditingProps = {
    header?: DetailViewHeader
}

export function useDetailViewHeaderCoverImageInlineEditing({
    header,
}: UseDetailViewHeaderCoverImageInlineEditingProps) {
    const { fields, setHeaderCoverImage } = useDetailViewContext()!
    const { isAdmin } = useAppUserContext()
    const getIsFieldEditable = useIsFieldEditable()

    const coverImage = (header as DetailViewHeaderSpacious).coverImage ?? null
    const coverImageField = fields.find(
        (field) => field.api_name === (coverImage as DetailViewHeaderFieldRef)?.fieldApiName
    )

    const editableImageType = coverImage?.type === 'field' ? ('field' as const) : ('image' as const)

    let allowEditImage: boolean = false
    if (editableImageType === 'field') {
        allowEditImage = !!coverImageField && getIsFieldEditable(coverImageField)
    } else if (editableImageType === 'image') {
        // This requires editing the view layout, so it's only available to admins.
        allowEditImage = isAdmin
    }

    const onEditableImageFileChange = useCallback(
        (url?: string, filename?: string) => {
            const newValue: DetailViewHeaderFileRef | undefined = !!url
                ? { type: 'file', url, filename: filename ?? '' }
                : undefined
            setHeaderCoverImage(newValue)
        },
        [setHeaderCoverImage]
    )

    return {
        editableImageType,
        allowEditImage,
        onEditableImageFileChange,
        imageField: coverImageField,
    }
}
